<template>
  <validation-observer ref="newsAndUpdatesObserver">
    <div class="modal-card" style="position:relative;">
      <b-loading
          :is-full-page="false"
          :active="isLoading"
          :can-cancel="true"
      >
      </b-loading>
      <header class="modal-card-head">
        <h2 class="modal-card-title">
          {{ releaseNote && !copying ? 'Edit release note' : 'Add release note' }}
        </h2>
        <button class="modal-close is-large" @click="$emit('close')"></button>
      </header>
      <section class="modal-card-body">
        <form class="form-modal-dialog">
          <p>Release note</p>
          <validation-provider name="Release note" v-slot="{ errors }" rules="required|max:1024">
            <b-field :type="{ 'is-danger': errors.length }"
                     :message="errors + [ '&nbsp;' ]">
              <b-input name="title"
                       type="textarea" rows="8"
                       v-model="releaseNoteDescription" maxlength="1024"></b-input>
            </b-field>
          </validation-provider>
          <b-field>
            <p>Recipients (select recipients to receive the release note)</p>
          </b-field>
          <b-field>
            <ReleaseNoteGroup
                v-if="isDataReady"
                title="Contractor"
                :tiers="filteredTiers"
                :release-note="releaseNote"
                @filtering-tiers="filteringTiers"
                @change-tagged-tiers="changeContractorTaggedTiers"
                @change-switch="(value) => this.isContractor = value"
            />
          </b-field>
          <b-field>
            <ReleaseNoteGroup
                v-if="isDataReady"
                ref="refAdditionalUser"
                title="Additional user"
                :tiers="filteredTiers"
                :sections="filteredSections"
                :release-note="releaseNote"
                @filtering-tiers="filteringTiers"
                @filtering-sections="filteringSections"
                @change-tagged-tiers="changeAdditionalUserTaggedTiers"
                @change-tagged-sections="changeAdditionalUserTaggedSections"
                @change-switch="(value) => this.isAdditionalUser = value"
            />
          </b-field>

          <b-field class="form-contractor-switch" grouped>
            <validation-provider
                mode="aggressive"
           :rules="{requiredRecipient: {arg1: isContractor, arg2: isAdditionalUser}}"
                v-slot="{ errors }">
              <b-field>
                <b-switch
                    class="enh-switch"
                    v-model="isManager"
                    position="is-right"
                    type="is-green"
                >Manager
                </b-switch>
              </b-field>
              <span class="help" :class="{'is-danger': errors.length > 0}">{{ errors[0] }}</span>
            </validation-provider>
          </b-field>
        </form>
      </section>
      <footer class="modal-card-foot buttons is-right">
        <div>
          <button class="button button-cancel" type="button" @click="cancel">Cancel</button>
          <button class="button is-success"
                  @click="submit">submit</button>
        </div>
      </footer>
    </div>
  </validation-observer>
</template>

<script>
import _, { clone } from 'lodash';
import { mapMutations, mapState } from 'vuex';
import { getContractorTeamAllowedResources, postNewsAndUpdates } from '@/api/settings';
import ReleaseNoteGroup from '@/components/ReleaseNoteGroup.vue';
import * as MUTATIONS from '../../store/mutations';


export default {
  name: 'NewsAndUpdatesModal',
  components: {
    ReleaseNoteGroup,
  },
  props: ['releaseNote', 'copying'],
  async mounted() {
    this.isLoading = true;
    this.getTiers();
    this.filteredTiers = clone(this.tiers);
    await this.getSections();
    this.filteredSections = clone(this.sections);
    this.isDataReady = true;
    if (this.releaseNote) {
      if (!this.copying) {
        this.releaseNoteDescription = this.releaseNote.releaseNote;
      }
      const { recipient } = this.releaseNote;
      if (recipient) {
        if ('contractor' in recipient) {
          this.contractorTaggedTiers = this.getRecipientInfoByType('tiers', recipient.contractor.tiers ?? []);
        }
        if ('additionalUser' in recipient) {
          this.additionalUserTaggedTiers = this.getRecipientInfoByType('tiers', recipient.additionalUser.tiers ?? []);
          this.additionalUserTaggedSections = this.getRecipientInfoByType('sections', recipient.additionalUser.sections ?? []);
        }
        if ('manager' in recipient) {
          this.isManager = true;
        }
      }
    }
    this.isLoading = false;
  },
  data() {
    return {
      releaseNoteDescription: '',
      isAdditionalUser: false,
      isManager: false,
      modes: [
        { key: 'any', value: 'any' },
        { key: 'isAnyOf', value: 'is any of...' },
      ],
      filteredTiers: [],
      tiers: [],
      filteredSections: [],
      sections: [],
      payload: {
        releaseNote: '',
        manager: null,
        contractor: {},
        additionalUser: {},
      },
      contractorTaggedTiers: [],
      additionalUserTaggedTiers: [],
      additionalUserTaggedSections: [],
      isContractor: false,
      isLoading: false,
      updatedData: null,
      isDataReady: false,
    };
  },
  watch: {
  },
  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
  },
  methods: {
    ...mapMutations({ preloader: MUTATIONS.SET_PRELOADER }),
    getRecipientInfoByType(type, data) {
      const arrayObjects = [];
      if (type === 'tiers') {
        return data.map(
          (tierName) => this.tiers.find(
            (tierObj) => tierObj.name === tierName,
          ),
        ).filter((item) => item);
      }
      if (type === 'sections') {
        return data.map(
          (sectionName) => this.sections.find(
            (sectionObj) => sectionObj.name === sectionName,
          ),
        ).filter((item) => item);
      }
      return arrayObjects;
    },

    async submit() {
      const valid = await this.$refs.newsAndUpdatesObserver.validate();
      if (!valid) return;
      this.preloader(true);
      this.payload.releaseNote = this.releaseNoteDescription;
      if (this.isContractor) {
        if (this.contractorTaggedTiers.length > 0) {
          this.payload.contractor.tiers = this.contractorTaggedTiers.map(
            (item) => item.name,
          );
        } else {
          this.payload.contractor = {};
        }
      } else {
        delete this.payload.contractor;
      }

      if (this.isAdditionalUser) {
        if (this.additionalUserTaggedTiers.length > 0) {
          this.payload.additionalUser.tiers = this.additionalUserTaggedTiers.map(
            (item) => item.name,
          );
        } else {
          delete this.payload.additionalUser.tiers;
        }
        if (this.additionalUserTaggedSections.length > 0) {
          this.payload.additionalUser.sections = this.additionalUserTaggedSections.map(
            (item) => item.name,
          );
        } else {
          delete this.payload.additionalUser.sections;
        }
      } else {
        delete this.payload.additionalUser;
      }
      if (this.isManager) {
        this.payload.manager = {};
      } else {
        delete this.payload.manager;
      }
      if (this.releaseNote && !this.copying) this.payload.id = this.releaseNote.id;

      await postNewsAndUpdates(this.payload)
        .then((response) => {
          if (response.data) {
            this.updatedData = response.data;
          }
        })
        .catch((err) => {
          const error = _.get(err, 'response.data.error', null);
          const message = _.get(err, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
          if (error) {
            this.$buefy.toast.open({ message, type: 'is-danger', duration: 3000 });
          } else {
            this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com' });
          }
        })
        .finally(() => {
          this.preloader(false);
        });
      this.$emit('submit', { context: this, updatedData: this.updatedData });
      this.$emit('close');
      this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success', duration: 3000 });
    },
    cancel() {
      this.$emit('close');
    },
    getTiers() {
      this.tiers = [
        { id: 'Pro', name: 'Pro' },
        { id: 'Self', name: 'Self' },
        { id: 'Concierge', name: 'Concierge' },
        { id: 'Brand', name: 'Brand' },
        { id: 'Free', name: 'Free' },
        { id: 'Test', name: 'Test' },
      ];
    },
    async getSections() {
      const { data } = await getContractorTeamAllowedResources();
      this.sections = data.data;
    },
    changeContractorTaggedTiers(contractorTiers) {
      this.contractorTaggedTiers = contractorTiers;
    },
    changeAdditionalUserTaggedTiers(additionalUserTiers) {
      this.additionalUserTaggedTiers = additionalUserTiers;
    },
    changeAdditionalUserTaggedSections(additionalUserSections) {
      this.additionalUserTaggedSections = additionalUserSections;
    },
    filteringTiers(value) {
      this.filteredTiers = this.tiers.filter((option) => option.name
        .toString()
        .toLowerCase()
        .indexOf(value.toString().toLowerCase()) >= 0);
    },
    filteringSections(value) {
      this.filteredSections = this.sections.filter((option) => option.name
        .toString()
        .toLowerCase()
        .indexOf(value.toString().toLowerCase()) >= 0);
    },
  },
};
</script>

<style scoped lang="stylus">
.form-modal-dialog >>> .form-contractor-switch
  justify-content space-between
.form-modal-dialog >>> .form-additional-user-switch
  justify-content space-between
button.button
  width 135px
  height 46px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.button.is-success
  border none
.isDanger
  color #DE4B4B
.padding-top-0
  padding-top 10px
.is-checked .span-used-distribution-set
  text-decoration line-through

  .field >>> .label {
    margin-bottom: 0;
  }
  .field >>> .help {
    margin-top: 0;
  }
.modal-card .modal-card-title
  font-weight: 600;
  margin-bottom unset

  .buttons:last-child {
    margin-bottom: unset;
  }

  .field >>> .textarea {
    padding: 0;
  }

  .modal-card-body {
    padding: 0 20px;
    max-height: 800px;
  }

</style>
